import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import * as tlsProfileSelectors from 'data/tlsProfile/tlsProfile.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as certStatusSelectors from 'data/certStatus/certStatus.selectors';
import { RootState } from 'data/root.reducers';
import * as tlsProfileActions from 'data/tlsProfile/tlsProfile.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as certStatusActions from 'data/certStatus/certStatus.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { TlsProfile } from 'data/tlsProfile/tlsProfile.types';
import TLSProfileServerForm from 'routes/TlsProfile/components/TLSProfileServerForm';
import * as types from 'data/tlsProfile/tlsProfile.types';
import { CertStatus, CertStatusGridData } from 'data/certStatus/certStatus.types';
import { SelectionChangedEvent } from '@ag-grid-community/core';
import { adaptCertStatusGridData } from 'data/tlsProfile/tlsProfile.adapter';
import { tlsProfileTemplate } from '../../templates';
import {
  adaptTemplateOptions, adaptTemplateVisibilityMode, adaptTlsProfileFormData, adaptTemplateOnLoad
} from '../../utils/tlsProfileTemplate.adapter';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.TlsProfile
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedTlsProfile: tlsProfileSelectors.getSelectedTlsProfile(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedTlsProfile: tlsProfileSelectors.getSelectedTlsProfile(state)[0],
  allCertStatuses: certStatusSelectors.getAllCertStatus(state),
  tlsProfile: tlsProfileSelectors.getTlsProfile(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(state),
});

const mapDispatch = {
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToTlsProfileList: tlsProfileActions.goToTlsProfileList,
  goToTlsProfileEdit: tlsProfileActions.goToTlsProfileEdit,
  getTlsProfile: tlsProfileActions.getTlsProfile,
  getAllCertStatus: certStatusActions.getAllCertStatus,
  activateCertStatus: certStatusActions.activateCertStatus,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const TLSProfileView = ({
  history,
  selectedTlsProfile,
  deliveryFunctionId,
  tlsProfile,
  goToTlsProfileList,
  goToTlsProfileEdit,
  getTlsProfile,
  allCertStatuses,
  enabledAccessFunctions,
  accessFunctionTemplates,
  getAllCertStatus
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<TlsProfile>>({});
  const [template, setTemplate] = useState(adaptTemplateVisibilityMode(tlsProfileTemplate, 'view'));
  const [certStatusGridData, setCertStatusGridData] = useState<CertStatusGridData[]>([]);
  const [selectedCertStatus, setSelectedCertStatus] = useState<CertStatusGridData>();
  const { t } = useTranslation();

  const id = selectedTlsProfile?.id;

  useEffect(() => {
    if (id) {
      getTlsProfile(deliveryFunctionId, id);
      getAllCertStatus(deliveryFunctionId);
    }
  }, [deliveryFunctionId, id, getTlsProfile, getAllCertStatus]);

  useEffect(() => {
    if (tlsProfile != null && template != null) {
      let clonedTlsProfile = cloneDeep(tlsProfile);
      let clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      clonedTlsProfile = adaptTlsProfileFormData(clonedTlsProfile);
      clonedTemplate = adaptTemplateOnLoad(clonedTemplate, clonedTlsProfile);
      setTemplate(adaptTemplateOptions(clonedTemplate, enabledAccessFunctions, accessFunctionTemplates, tlsProfile));
      setFormData(clonedTlsProfile);
      setCertStatusGridData(adaptCertStatusGridData(allCertStatuses, tlsProfile));
    }
  }, [tlsProfile, allCertStatuses, enabledAccessFunctions, accessFunctionTemplates, id]);

  const handleClickEdit = () => goToTlsProfileEdit(history);

  const handleCancel = () => goToTlsProfileList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as TlsProfile);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const handleRefreshCertStatuses = () => {
    getAllCertStatus(deliveryFunctionId);
  }

  const handleCertStatusSelectionChanged = (event: SelectionChangedEvent) => {
    setSelectedCertStatus(event.api.getSelectedRows()[0]);
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <TLSProfileServerForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
          certGridData={certStatusGridData}
          handleRefreshCertStatuses={handleRefreshCertStatuses}
          handleCertStatusSelectionChanged={handleCertStatusSelectionChanged}
          selectedCertStatus={selectedCertStatus}
        />
      );
    }
  };
  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleClickEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (tlsProfile == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="TLSProfileView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('tlsProfile.view.title'))}
          <div className="read-only">
            {renderForm()}
          </div>
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(TLSProfileView));
